"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { useIsMobile } from "../../hooks/use-mobile"
import { useSidebarReflowZoom } from "../../hooks/use-sidebar-reflow-zoom"
import {
  NAV_FLYOUT_CHROME,
  NAV_FLYOUT_INNER,
  NAV_FLYOUT_INSET_LEFT,
  NAV_FLYOUT_INSET_RIGHT,
  NAV_FLYOUT_INSET_Y,
} from "../../lib/nav-flyout-inset"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Separator } from "./separator"
import { Skeleton } from "./skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./tooltip"

/**
 * Cookie persisting the user's explicit expanded/collapsed preference.
 *
 * Versioned (`_v2`) on 2026-05-21 to drop stale values written by the
 * pre-fix code where incidental layout collapses (secondary panel open,
 * route auto-collapse) would clobber the user's preference on every
 * navigation. Anyone on the old `sidebar_state` cookie falls back to
 * the `defaultOpen` default (expanded), and the next explicit toggle
 * starts persisting under the new name. The old cookie self-expires
 * by `max-age` within a week; if you want immediate cleanup, the
 * client clears it explicitly on first mount (see `useLayoutEffect`
 * below).
 */
const SIDEBAR_COOKIE_NAME = "sidebar_state_v2"
const SIDEBAR_COOKIE_LEGACY_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
/** Matches `useIsMobile` / Tailwind `md:` — do not apply desktop cookie to mobile overlay. */
const SIDEBAR_COOKIE_VIEWPORT_MQ = "(max-width: 767px)"
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_ICON = "3rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

/**
 * Options for {@link SidebarContextProps.setOpen}.
 *
 * `persist: false` updates the visual sidebar state **without** writing the
 * `sidebar_state` cookie. Pass it from incidental layout effects — e.g. a
 * secondary panel taking the rail space, a route-level auto-collapse — so
 * the user's explicit preference (their ⌘B toggle / sidebar button) is the
 * only thing that ever rewrites the cookie. Defaults to `true` for
 * backward compatibility with the toggle button and existing callsites.
 */
type SetOpenOptions = { persist?: boolean }

/** Return `true` when the handler consumed ⌘B / sidebar trigger (skip primary toggle). */
export type NavFlyoutToggleHandler = () => boolean

type SidebarContextProps = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (
    open: boolean | ((value: boolean) => boolean),
    opts?: SetOpenOptions,
  ) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  /** Mobile or browser zoom ≥ ~200% — sidebar renders as a dismissible flyout. */
  isNavFlyout: boolean
  toggleSidebar: () => void
  /**
   * Register a flyout toggle override (secondary panel, drill-in, …).
   * Handlers run in registration order; first `true` wins.
   */
  registerNavFlyoutToggle: (handler: NavFlyoutToggleHandler) => () => void
  /** Close the flyout after the user picks a nav destination (mobile / high zoom). */
  dismissNavFlyout: () => void
  /**
   * Snap the rail back to the user's saved preference — the value persisted in
   * `sidebar_state_v2`, or `defaultOpen` if no cookie was ever written. Use
   * this from incidental-collapse cleanups (secondary panel closing, route
   * leaves) so the rail doesn't stay stuck in its incidental state once the
   * thing that caused the collapse is gone. Always uses `persist: false`.
   */
  restoreSavedOpen: () => void
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

function readSidebarStateCookie(): boolean | undefined {
  if (typeof document === "undefined") return undefined
  const m = document.cookie.match(new RegExp(`(?:^|; )${SIDEBAR_COOKIE_NAME}=(true|false)(?:;|$)`))
  if (!m) return undefined
  return m[1] === "true"
}

function useNavFlyout() {
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  return isMobile || reflowZoom
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const isMobile = useIsMobile()
  const isNavFlyout = useNavFlyout()
  const navFlyoutToggleHandlersRef = React.useRef<Set<NavFlyoutToggleHandler> | null>(null)
  if (navFlyoutToggleHandlersRef.current === null) {
    navFlyoutToggleHandlersRef.current = new Set()
  }
  const [openMobile, setOpenMobile] = React.useState(false)

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const openRef = React.useRef(open)
  React.useEffect(() => {
    openRef.current = open
  })

  // `setOpen` already persists `sidebar_state_v2` to a cookie on desktop; restore it on mount so
  // full reloads and new tabs keep the rail expanded/collapsed. Skip when controlled or on mobile.
  // Also actively drop the legacy `sidebar_state` cookie (pre-2026-05-21) so stale
  // collapsed-by-default values written by the old incidental-collapse bug don't linger.
  //
  // MOUNT-ONLY: deps are `[]` so this runs once per `SidebarProvider` mount, NOT
  // every time `open` flips. That matters because incidental collapses
  // (`setOpen(false, { persist: false })` from a secondary panel opening or
  // `SidebarAutoCollapse` route) must NOT trigger a re-reconcile that reads the
  // saved cookie back as "expanded" and snaps the rail open again.
  // The functional setter avoids closing over a stale `open` value.
  // Deps are `[openProp]` only — not `open` — so incidental collapses do not re-read the cookie.
  React.useLayoutEffect(() => {
    if (typeof window === "undefined") return
    if (typeof document !== "undefined" && document.cookie.includes(`${SIDEBAR_COOKIE_LEGACY_NAME}=`)) {
      document.cookie = `${SIDEBAR_COOKIE_LEGACY_NAME}=; path=/; max-age=0`
    }
    if (openProp !== undefined) return
    if (window.matchMedia(SIDEBAR_COOKIE_VIEWPORT_MQ).matches) return
    const fromCookie = readSidebarStateCookie()
    if (fromCookie === undefined) return
    _setOpen((prev) => (prev === fromCookie ? prev : fromCookie))
  }, [openProp])

  const setOpen = React.useCallback(
    (
      value: boolean | ((value: boolean) => boolean),
      opts?: SetOpenOptions,
    ) => {
      const openState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      // Persist on desktop only — zooming in and closing shouldn't clobber the
      // desktop state. Callers can also opt out explicitly (`persist: false`)
      // for incidental layout collapses such as a secondary panel opening or
      // an auto-collapse route; those visual side-effects must not overwrite
      // the user's saved expanded/collapsed preference.
      if (!isNavFlyout && opts?.persist !== false) {
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
      }
    },
    [setOpenProp, open, isNavFlyout]
  )

  // Mobile / high-zoom flyout: never leave the overlay open just because the
  // desktop cookie says expanded — that blocks the page on first paint after
  // zoom ≥ 200% or a narrow viewport. Restore the pre-flyout state on exit.
  const wasNavFlyoutRef = React.useRef(isNavFlyout)
  const savedOpenBeforeFlyoutRef = React.useRef<boolean | null>(null)
  React.useLayoutEffect(() => {
    const wasNavFlyout = wasNavFlyoutRef.current
    if (isNavFlyout === wasNavFlyout) return

    const entering = isNavFlyout && !wasNavFlyout
    const leaving = !isNavFlyout && wasNavFlyout

    if (entering) {
      savedOpenBeforeFlyoutRef.current = openRef.current
      // Always start closed in flyout — user opens via trigger; click-outside / Esc closes.
      setOpen(false, { persist: false })
    }
    if (leaving && savedOpenBeforeFlyoutRef.current !== null) {
      setOpen(savedOpenBeforeFlyoutRef.current, { persist: false })
      savedOpenBeforeFlyoutRef.current = null
    }
    wasNavFlyoutRef.current = isNavFlyout
  }, [isNavFlyout, setOpen])

  const registerNavFlyoutToggle = React.useCallback(
    (handler: NavFlyoutToggleHandler) => {
      const handlers = navFlyoutToggleHandlersRef.current
      if (!handlers) return () => {}
      handlers.add(handler)
      return () => {
        handlers.delete(handler)
      }
    },
    [],
  )

  // Helper to toggle the sidebar (primary flyout unless a nested handler wins).
  const toggleSidebar = React.useCallback(() => {
    if (isNavFlyout) {
      const handlers = navFlyoutToggleHandlersRef.current
      if (handlers) {
        for (const handler of handlers) {
          if (handler()) return
        }
      }
    }
    setOpen((open) => !open)
  }, [isNavFlyout, setOpen])

  /**
   * Snap the rail back to the user's saved preference. Called when a secondary
   * panel closes or an auto-collapse route is left — so an incidental collapse
   * doesn't persist beyond the page that caused it. `persist: false` because
   * this restore is itself incidental; the only writes to the cookie should
   * still come from real user gestures (⌘B, sidebar button).
   */
  const restoreSavedOpen = React.useCallback(() => {
    if (typeof window === "undefined") return
    const fromCookie = readSidebarStateCookie()
    const target = fromCookie ?? defaultOpen
    setOpen(target, { persist: false })
  }, [defaultOpen, setOpen])

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        toggleSidebar()
        return
      }
      // WCAG 2.1.1 — Escape closes the floating sidebar on mobile/high-zoom
      if (event.key === "Escape" && isNavFlyout && open) {
        event.preventDefault()
        setOpen(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar, isNavFlyout, open, setOpen])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed"

  const dismissNavFlyout = React.useCallback(() => {
    if (isNavFlyout && open) {
      setOpen(false, { persist: false })
    }
  }, [isNavFlyout, open, setOpen])

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      isNavFlyout,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      registerNavFlyoutToggle,
      restoreSavedOpen,
      dismissNavFlyout,
    }),
    [state, open, setOpen, isMobile, isNavFlyout, openMobile, setOpenMobile, toggleSidebar, registerNavFlyoutToggle, restoreSavedOpen, dismissNavFlyout]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        data-slot="sidebar-wrapper"
        data-state={state}
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          // min-h-svh: shell is at least viewport-tall so the main inset can stretch when page
          // content is short. Inner layout rows use min-h-0 where scroll containment is needed.
          "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  dir: _dir,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right"
  variant?: "sidebar" | "floating" | "inset"
  collapsible?: "offcanvas" | "icon" | "none"
}) {
  const { state, setOpen } = useSidebar()
  const isNavFlyout = useNavFlyout()

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "group peer text-sidebar-foreground",
        // Always in the DOM so peer/group selectors work; gap is hidden on mobile
        isNavFlyout ? "block" : "hidden md:block",
      )}
      data-state={state}
      data-collapsible={state === "collapsed" ? (isNavFlyout ? "offcanvas" : collapsible) : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      data-nav-flyout={isNavFlyout ? "true" : undefined}
    >
      {/* Layout gap — pushes page content right on desktop; zero on mobile (sidebar floats) */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          isNavFlyout
            ? "hidden"
            : cn(
                "relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
                "group-data-[collapsible=offcanvas]:w-0",
                "group-data-[side=right]:rotate-180",
                variant === "floating" || variant === "inset"
                  ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
                  : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
              )
        )}
      />
      {/* Mobile / high-zoom scrim — closes the floating sidebar when tapping outside */}
      {isNavFlyout && state === "expanded" && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 cursor-default bg-background/60"
          onClick={() => setOpen(false, { persist: false })}
        />
      )}
      <div
        data-slot="sidebar-container"
        data-side={side}
        className={cn(
          "fixed w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear",
          // Desktop
          !isNavFlyout && cn(
            "hidden inset-y-0 h-svh z-0 md:flex",
            "data-[side=left]:left-0 data-[side=right]:right-0",
            "data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]",
            "data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
              : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-e group-data-[side=right]:border-s",
          ),
          // Mobile / high-zoom: floating panel with rounded corners and inset margins
          isNavFlyout && cn(
            "flex min-h-0 flex-col z-50 overflow-hidden bg-sidebar",
            NAV_FLYOUT_INSET_Y,
            side === "left"
              ? cn(
                  NAV_FLYOUT_INSET_LEFT,
                  "group-data-[collapsible=offcanvas]:left-[calc(-1*(var(--sidebar-width)+0.5rem))]",
                )
              : cn(
                  NAV_FLYOUT_INSET_RIGHT,
                  "group-data-[collapsible=offcanvas]:right-[calc(-1*(var(--sidebar-width)+0.5rem))]",
                ),
            NAV_FLYOUT_CHROME,
          ),
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className={cn(
            "flex size-full min-h-0 flex-col bg-sidebar",
            isNavFlyout && NAV_FLYOUT_INNER,
            !isNavFlyout && "group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-sidebar-border",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon-sm"
      className={cn("shrink-0", className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <i className="fa-light fa-sidebar rtl:rotate-180" aria-hidden="true" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      type="button"
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-10 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-sidebar-border sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2",
        "in-data-[side=left]:cursor-w-resize rtl:in-data-[side=left]:cursor-e-resize in-data-[side=right]:cursor-e-resize rtl:in-data-[side=right]:cursor-w-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize rtl:[[data-side=left][data-state=collapsed]_&]:cursor-w-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize rtl:[[data-side=right][data-state=collapsed]_&]:cursor-e-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 rtl:group-data-[collapsible=offcanvas]:-translate-x-0 group-data-[collapsible=offcanvas]:after:start-full hover:group-data-[collapsible=offcanvas]:bg-sidebar",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-end-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-start-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        // flex-1 + min-h-0: stretch with the app row so main is at least viewport height (minus banner)
        // when content is short; inner column scrolls via min-h-0 flex children.
        // Below md (incl. desktop at high zoom): symmetric horizontal inset so the card is not flush left.
        // md+ inset variant: ms-0 aligns with the sidebar gap; collapsed rail adds ms-2.
        "relative z-30 flex min-h-0 w-full min-w-0 flex-1 flex-col self-stretch bg-background pb-6 rounded-xl shadow-sm outline-none focus:outline-none focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 my-1.5 mx-2 md:peer-data-[variant=inset]:my-2 md:peer-data-[variant=inset]:mx-2 md:peer-data-[variant=inset]:ms-0 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ms-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn("h-8 w-full bg-background shadow-none", className)}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn("flex shrink-0 flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  const { isNavFlyout } = useSidebar()

  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn(
        "flex shrink-0 flex-col gap-2 p-2",
        /* Flyout rounded corners clip the bottom edge — keep profile targets ≥24px clear. */
        isNavFlyout && "pb-3.5",
        className,
      )}
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-sidebar-border", className)}
      {...props}
    />
  )
}

/** Horizontal rule inside {@link SidebarMenu} — must be an `<li>` for valid list markup. */
function SidebarMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SidebarSeparator>) {
  return (
    <li
      data-slot="sidebar-menu-separator"
      data-sidebar="menu-separator"
      role="presentation"
      aria-hidden="true"
      className={cn(
        "group/menu-item relative",
        "group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center",
      )}
    >
      <SidebarSeparator className={className} {...props} />
    </li>
  )
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"p"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "p"

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "m-0 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-section-label ring-sidebar-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:hidden focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "absolute top-3.5 end-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-0", className)}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      suppressHydrationWarning
      className={cn(
        "group/menu-item relative",
        /* Icon rail: center the square menu control in the column (footer + primary). */
        "group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center",
        className,
      )}
      {...props}
    />
  )
}

/** Visible label for primary / secondary sidebar rows — MUST NOT truncate. */
function SidebarNavLabel({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-sidebar-nav-label
      className={cn("min-w-0 flex-1 whitespace-normal break-words", className)}
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button group/menu-button flex w-full group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-8 cursor-pointer select-none items-center gap-2 overflow-visible group-data-[collapsible=icon]:overflow-hidden rounded-md px-2 py-1 text-start text-sm ring-sidebar-ring outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pe-8 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!min-h-8 group-data-[collapsible=icon]:!max-h-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:aspect-square group-data-[collapsible=icon]:shrink-0 group-data-[collapsible=icon]:p-2! hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground data-active:bg-background data-active:font-medium data-active:text-foreground data-active:shadow-sm data-active:ring-1 data-active:ring-sidebar-border data-active:hover:bg-background data-active:hover:text-foreground data-active:hc:border data-active:hc:border-foreground data-active:forced-colors:border data-active:forced-colors:border-[Highlight] [&_svg:not([data-product-logo]):not([data-product-logo-mark])]:size-4 [&_svg]:shrink-0 [&>span[aria-hidden]:first-child_i]:block [&>span[aria-hidden]:first-child_i]:leading-none [&>i[aria-hidden].fa-chevron-right]:block [&>i[aria-hidden].fa-chevron-right]:leading-none [&_[data-sidebar-nav-label]]:min-w-0 [&_[data-sidebar-nav-label]]:whitespace-normal [&_[data-sidebar-nav-label]]:break-words [&_[data-sidebar-nav-label]]:overflow-visible group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:[&>*:not(:first-child)]:hidden",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "min-h-7 h-auto text-sm",
        sm: "min-h-7 h-auto text-xs",
        lg: "min-h-12 h-auto text-sm group-data-[collapsible=icon]:p-0! group-data-[nav-flyout=true]:min-h-[3.25rem]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & {
    asChild?: boolean
    isActive?: boolean
    tooltip?: string | React.ReactElement | React.ComponentProps<typeof TooltipContent>
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(function SidebarMenuButton(
  {
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    tooltip,
    className,
    "aria-label": ariaLabelProp,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button"
  const { isMobile, state } = useSidebar()

  const collapsedIconLabel =
    typeof tooltip === "string" &&
    !ariaLabelProp &&
    state === "collapsed" &&
    !isMobile
      ? tooltip
      : undefined

  const button = (
    <Comp
      ref={ref}
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive || undefined}
      className={cn(
        sidebarMenuButtonVariants({ variant, size }),
        className,
        // `asChild` merges the child (e.g. Next `<Link className="w-full">`) onto this node —
        // plain `w-full` can win over `group-data-[collapsible=icon]:w-8` in the stylesheet and
        // squash the icon rail to a non-square hit target (WCAG 2.5.8).
        "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-h-8 group-data-[collapsible=icon]:!max-h-8 group-data-[collapsible=icon]:aspect-square group-data-[collapsible=icon]:shrink-0",
      )}
      aria-label={ariaLabelProp ?? collapsedIconLabel}
      {...props}
    />
  )

  if (!tooltip) {
    return button
  }

  const tooltipContentProps: React.ComponentProps<typeof TooltipContent> =
    typeof tooltip === "string"
      ? { children: tooltip }
      : React.isValidElement(tooltip)
        ? { children: tooltip }
        : tooltip

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltipContentProps}
      />
    </Tooltip>
  )
})

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  showOnHover?: boolean
}) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "absolute top-1.5 end-1 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:text-sidebar-accent-foreground aria-expanded:opacity-100 md:opacity-0",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "pointer-events-none absolute end-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 peer-data-active/menu-button:text-sidebar-accent-foreground",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`
  })

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "flex w-full min-w-0 flex-col gap-1 py-[var(--exxat-spacing-1)] pe-0 group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn(
        "group/menu-sub-item relative flex w-full min-w-0 items-center",
        className,
      )}
      {...props}
    />
  )
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean
  size?: "sm" | "md"
  isActive?: boolean
}) {
  const Comp = asChild ? Slot.Root : "a"

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive || undefined}
      className={cn(
        "flex w-full min-h-7 h-auto min-w-0 flex-1 cursor-pointer select-none items-center gap-2 overflow-visible rounded-md px-2 py-1 text-sidebar-foreground ring-sidebar-ring outline-hidden group-data-[collapsible=icon]:hidden group-data-[collapsible=icon]:items-center hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-sm data-[size=sm]:text-xs data-active:bg-background data-active:text-foreground data-active:shadow-sm data-active:ring-1 data-active:ring-inset data-active:ring-sidebar-border data-active:hc:border data-active:hc:border-foreground data-active:forced-colors:border data-active:forced-colors:border-[Highlight] [&_[data-sidebar-nav-label]]:min-w-0 [&_[data-sidebar-nav-label]]:whitespace-normal [&_[data-sidebar-nav-label]]:break-words [&_[data-sidebar-nav-label]]:overflow-visible [&>span:first-child]:flex [&>span:first-child]:size-4 [&>span:first-child]:shrink-0 [&>span:first-child]:items-center [&>span:first-child]:justify-center [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSeparator,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarNavLabel,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}

/** Register a mobile / high-zoom flyout toggle override while mounted. */
export function useRegisterNavFlyoutToggle(handler: NavFlyoutToggleHandler | null) {
  const { registerNavFlyoutToggle } = useSidebar()
  const handlerRef = React.useRef(handler)
  React.useEffect(() => {
    handlerRef.current = handler
  })

  React.useEffect(() => {
    if (!handlerRef.current) return
    const wrapped: NavFlyoutToggleHandler = () => handlerRef.current?.() ?? false
    return registerNavFlyoutToggle(wrapped)
  }, [registerNavFlyoutToggle])
}
